// Columna izquierda del explorer (`.col-filters` del diseño): Búsqueda · Canal · Fecha · Monto.
// La búsqueda va con debounce; el resto sube al padre en cuanto cambia.
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { activeFilterCount, dateShortcutRange } from '../logic';
import { PROMISE_CHANNEL_FILTER_LABEL } from '../format';
import { ANY_FILTER, emptyPromisesFilters, PROMISE_CHANNELS, type PromisesFilters } from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

/** Los cuatro atajos de fecha del diseño. Negativo es «Todo»: limpia el rango. */
const DATE_SHORTCUTS: { label: string; days: number }[] = [
    { label: 'Hoy', days: 0 },
    { label: 'Mañana', days: 1 },
    { label: '7d', days: 7 },
    { label: 'Todo', days: -1 },
];

/** Un campo numérico en PESOS que sube a centavos; vacío es `null` («sin tope»), no cero. */
function MoneyInput({
    value,
    placeholder,
    ariaLabel,
    onChange,
}: {
    value: number | null;
    placeholder: string;
    ariaLabel: string;
    onChange: (cents: number | null) => void;
}) {
    return (
        <input
            type="number"
            min={0}
            inputMode="numeric"
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={value === null ? '' : String(value / 100)}
            onChange={(event) => {
                const raw = event.target.value.trim();
                onChange(raw === '' ? null : Math.round(Number(raw) * 100));
            }}
            className={cn(FIELD, 'tabular-nums')}
        />
    );
}

export interface PromisesFiltersPanelProps {
    filters: PromisesFilters;
    onChange: (next: PromisesFilters) => void;
    /** El día contra el que miden los atajos de fecha, en `YYYY-MM-DD`. */
    today: string;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function PromisesFiltersPanel({
    filters,
    onChange,
    today,
    collapsed,
    onToggleCollapsed,
}: PromisesFiltersPanelProps) {
    const [searchText, setSearchText] = useState(filters.search);
    const debouncedSearch = useDebounce(searchText, 250);
    const emittedSearch = useRef(filters.search);

    // Sube la búsqueda ya reposada; el guard evita re-emitir cuando cambia otro filtro.
    useEffect(() => {
        if (debouncedSearch === emittedSearch.current) return;
        emittedSearch.current = debouncedSearch;
        onChange({ ...filters, search: debouncedSearch });
    }, [debouncedSearch, filters, onChange]);

    // Baja a la caja local un reset hecho desde afuera.
    useEffect(() => {
        if (filters.search === emittedSearch.current) return;
        emittedSearch.current = filters.search;
        setSearchText(filters.search);
    }, [filters.search]);

    const patch = (partial: Partial<PromisesFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const clearAll = () => {
        emittedSearch.current = '';
        setSearchText('');
        onChange(emptyPromisesFilters());
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeFilterCount(filters)}
            onReset={clearAll}
            regionLabel="Filtros"
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                        placeholder="Cliente, PR-XXXX o CR-XXXX"
                        aria-label="Buscar por cliente, compromiso o crédito"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {searchText === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                emittedSearch.current = '';
                                setSearchText('');
                                patch({ search: '' });
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Canal</div>
                <select
                    value={filters.channel}
                    aria-label="Canal"
                    onChange={(event) => {
                        patch({ channel: event.target.value as PromisesFilters['channel'] });
                    }}
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {PROMISE_CHANNELS.map((channel) => (
                        <option key={channel} value={channel}>
                            {PROMISE_CHANNEL_FILTER_LABEL[channel]}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Fecha</div>
                <div className="grid grid-cols-2 gap-1">
                    <input
                        type="date"
                        value={filters.fromDay}
                        aria-label="Prometido desde"
                        onChange={(event) => {
                            patch({ fromDay: event.target.value });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <input
                        type="date"
                        value={filters.toDay}
                        aria-label="Prometido hasta"
                        onChange={(event) => {
                            patch({ toDay: event.target.value });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
                {/* Los cuatro en fila, cada uno del ancho de su rótulo: en cuartos iguales
                    «Mañana» no entra y sale cortado. Es el mismo reparto que usa Cartera. */}
                <div className="mt-1 flex flex-wrap gap-1">
                    {DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch(dateShortcutRange(shortcut.days, today));
                            }}
                            className="whitespace-nowrap rounded border border-border px-1.5 py-[2px] text-[10px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                            {shortcut.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Monto</div>
                <div className="grid grid-cols-2 gap-1">
                    <MoneyInput
                        value={filters.minAmountCents}
                        placeholder="Mín"
                        ariaLabel="Monto mínimo"
                        onChange={(cents) => {
                            patch({ minAmountCents: cents });
                        }}
                    />
                    <MoneyInput
                        value={filters.maxAmountCents}
                        placeholder="Máx"
                        ariaLabel="Monto máximo"
                        onChange={(cents) => {
                            patch({ maxAmountCents: cents });
                        }}
                    />
                </div>
            </div>
        </FiltersColumn>
    );
}
